import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Save, GripVertical } from "lucide-react";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { useDragReorder } from "@/hooks/useDragReorder";
import TranslateButton from "@/components/TranslateButton";
import ImageUploader, { resolveAssetUrl } from "@/components/ImageUploader";

export default function AdminJourneyTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { data: journey, isLoading } = useQuery({ queryKey: ["journey"], queryFn: endpoints.journey });
  const [editing, setEditing] = useState(null);

  const refetch = () => qc.invalidateQueries({ queryKey: ["journey"] });

  const onSaveStage = async (form) => {
    if (!editing) return;
    try {
      if (editing.id === "new") {
        await endpoints.adminAddJourneyStage(form);
        toast.success(isRTL ? "تمت الإضافة" : "Added");
      } else {
        await endpoints.adminUpdateJourneyStage(editing.id, form);
        toast.success(isRTL ? "تم الحفظ" : "Saved");
      }
      refetch();
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذه المرحلة؟" : "Delete this stage?")) return;
    try {
      await endpoints.adminDeleteJourneyStage(id);
      toast.success(isRTL ? "تم الحذف" : "Deleted");
      refetch();
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const move = async (ids) => {
    try {
      await endpoints.adminReorderJourney(ids);
      refetch();
    } catch {
      toast.error(isRTL ? "تعذّر الترتيب" : "Reorder failed");
    }
  };

  if (isLoading) return <div className="text-[#5C544F]">…</div>;
  const stages = journey?.stages || [];

  return (
    <div className="space-y-6" data-testid="admin-journey-tab">
      <HeadEditor initial={journey} onSaved={refetch} />

      <div className="rounded-2xl border border-[#2A2421]/10 bg-white overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[#2A2421]/8">
          <h3 className="font-semibold text-[#2A2421]">
            {isRTL ? "مراحل الرحلة" : "Journey Stages"} <span className="text-xs text-[#5C544F]">· {stages.length}</span>
          </h3>
          <Button size="sm" className="bg-[#C86240] hover:bg-[#b15637] text-white rounded-full" onClick={() => setEditing({ id: "new" })} data-testid="journey-add-stage">
            <Plus className="w-3.5 h-3.5 me-1" /> {isRTL ? "إضافة" : "Add"}
          </Button>
        </div>
        <p className="text-[10px] text-[#5C544F] px-4 py-2">
          {isRTL ? "اسحب من مقبض ⋮⋮ لإعادة الترتيب" : "Drag the ⋮⋮ handle to reorder"}
        </p>
        <StagesList stages={stages} onMove={move} setEditing={setEditing} onDelete={onDelete} isRTL={isRTL} />
      </div>

      {editing && (
        <StageDialog
          key={editing.id}
          initial={editing.id !== "new" ? editing : null}
          onClose={() => setEditing(null)}
          onSave={onSaveStage}
        />
      )}
    </div>
  );
}

function StagesList({ stages, onMove, setEditing, onDelete, isRTL }) {
  const { getDragProps } = useDragReorder(stages, onMove);
  return (
    <div className="p-3 space-y-2">
      {stages.map((s) => (
        <div
          key={s.id}
          {...getDragProps(s.id)}
          className="flex items-start gap-3 rounded-xl bg-[#F9F6F0] p-3 transition-all data-[dragging=true]:opacity-50 data-[drag-over=true]:bg-[#C86240]/10 data-[drag-over=true]:ring-1 data-[drag-over=true]:ring-[#C86240]"
          data-testid={`journey-stage-${s.id}`}
        >
          <div className="flex items-center text-[#5C544F] hover:text-[#2A2421] cursor-grab active:cursor-grabbing self-stretch">
            <GripVertical className="w-4 h-4" />
          </div>
          {s.image && <img src={resolveAssetUrl(s.image)} alt="" className="w-20 h-16 rounded-lg object-cover flex-shrink-0" />}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-[#C86240] font-semibold">{s.tag_en || s.tag_ar}</div>
            <div className="font-medium text-[#2A2421] text-sm">{s.title_en}</div>
            <div className="text-xs text-[#5C544F]" dir="rtl">{s.title_ar}</div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(s)} data-testid={`journey-edit-${s.id}`}>
              <Pencil className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(s.id)} data-testid={`journey-delete-${s.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      ))}
      {stages.length === 0 && <div className="text-center py-8 text-[#5C544F] text-sm">{isRTL ? "لا توجد مراحل" : "No stages yet"}</div>}
    </div>
  );
}


function HeadEditor({ initial, onSaved }) {
  const { isRTL } = useI18n();
  const [form, setForm] = useState({
    title_en: initial?.title_en || "",
    title_ar: initial?.title_ar || "",
    lead_en: initial?.lead_en || "",
    lead_ar: initial?.lead_ar || "",
  });
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    try {
      await endpoints.adminUpdateJourneyHead(form);
      toast.success(isRTL ? "تم الحفظ" : "Saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر الحفظ" : "Save failed"));
    }
  };
  return (
    <div className="rounded-2xl border border-[#2A2421]/10 bg-white p-4 space-y-3" data-testid="journey-head-editor">
      <h3 className="font-semibold text-[#2A2421]">{isRTL ? "ترويسة الرحلة" : "Journey Header"}</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={<span className="flex items-center justify-between w-full">Title (EN) <TranslateButton sourceText={form.title_ar} target="en" onTranslated={(v) => set("title_en", v)} testid="journey-head-title-en-translate" /></span>}><Input value={form.title_en} onChange={(e) => set("title_en", e.target.value)} /></Field>
        <Field label={<span className="flex items-center justify-between w-full">العنوان (AR) <TranslateButton sourceText={form.title_en} target="ar" onTranslated={(v) => set("title_ar", v)} testid="journey-head-title-ar-translate" /></span>}><Input value={form.title_ar} onChange={(e) => set("title_ar", e.target.value)} dir="rtl" /></Field>
      </div>
      <Field label={<span className="flex items-center justify-between w-full">Lead (EN) <TranslateButton sourceText={form.lead_ar} target="en" onTranslated={(v) => set("lead_en", v)} testid="journey-head-lead-en-translate" /></span>}><Textarea rows={2} value={form.lead_en} onChange={(e) => set("lead_en", e.target.value)} /></Field>
      <Field label={<span className="flex items-center justify-between w-full">المقدّمة (AR) <TranslateButton sourceText={form.lead_en} target="ar" onTranslated={(v) => set("lead_ar", v)} testid="journey-head-lead-ar-translate" /></span>}><Textarea rows={2} value={form.lead_ar} onChange={(e) => set("lead_ar", e.target.value)} dir="rtl" /></Field>
      <div className="flex justify-end">
        <Button onClick={save} className="bg-[#2A2421] text-white"><Save className="w-3.5 h-3.5 me-1" /> {isRTL ? "حفظ" : "Save"}</Button>
      </div>
    </div>
  );
}

function StageDialog({ initial, onClose, onSave }) {
  const { isRTL } = useI18n();
  const [form, setForm] = useState(initial || {});
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#F9F6F0]">
        <DialogHeader><DialogTitle className="text-start">{initial ? (isRTL ? "تعديل مرحلة" : "Edit stage") : (isRTL ? "مرحلة جديدة" : "New stage")}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pe-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={<span className="flex items-center justify-between w-full">Tag (EN) <TranslateButton sourceText={form.tag_ar} target="en" onTranslated={(v) => set("tag_en", v)} testid="journey-tag-en-translate" /></span>}><Input value={form.tag_en || ""} onChange={(e) => set("tag_en", e.target.value)} placeholder="01 · Cultivation" /></Field>
            <Field label={<span className="flex items-center justify-between w-full">الوسم (AR) <TranslateButton sourceText={form.tag_en} target="ar" onTranslated={(v) => set("tag_ar", v)} testid="journey-tag-ar-translate" /></span>}><Input value={form.tag_ar || ""} onChange={(e) => set("tag_ar", e.target.value)} dir="rtl" placeholder="01 · الزراعة" /></Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label={<span className="flex items-center justify-between w-full">Title (EN) <TranslateButton sourceText={form.title_ar} target="en" onTranslated={(v) => set("title_en", v)} testid="journey-title-en-translate" /></span>}><Input value={form.title_en || ""} onChange={(e) => set("title_en", e.target.value)} /></Field>
            <Field label={<span className="flex items-center justify-between w-full">العنوان (AR) <TranslateButton sourceText={form.title_en} target="ar" onTranslated={(v) => set("title_ar", v)} testid="journey-title-ar-translate" /></span>}><Input value={form.title_ar || ""} onChange={(e) => set("title_ar", e.target.value)} dir="rtl" /></Field>
          </div>
          <Field label={<span className="flex items-center justify-between w-full">Body (EN) <TranslateButton sourceText={form.body_ar} target="en" onTranslated={(v) => set("body_en", v)} testid="journey-body-en-translate" /></span>}><Textarea rows={4} value={form.body_en || ""} onChange={(e) => set("body_en", e.target.value)} /></Field>
          <Field label={<span className="flex items-center justify-between w-full">النص (AR) <TranslateButton sourceText={form.body_en} target="ar" onTranslated={(v) => set("body_ar", v)} testid="journey-body-ar-translate" /></span>}><Textarea rows={4} value={form.body_ar || ""} onChange={(e) => set("body_ar", e.target.value)} dir="rtl" /></Field>
          <Field label={isRTL ? "صورة المرحلة" : "Stage image"}>
            <ImageUploader
              value={form.image}
              onChange={(url) => set("image", url)}
              testid="journey-image-uploader"
              aspect="video"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[#2A2421]/10">
          <Button variant="ghost" onClick={onClose}>{isRTL ? "إلغاء" : "Cancel"}</Button>
          <Button onClick={() => onSave(form)} className="bg-[#2A2421] text-white">{isRTL ? "حفظ" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs text-[#5C544F] mb-1">{label}</span>
    {children}
  </label>
);
